import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Minus, Plus, ShoppingBag, Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useProduct, useSimilarProducts } from "@/hooks/useProducts";
import ProductCard from "@/components/product/ProductCard";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingCart from "@/components/layout/FloatingCart";
import FloatingWhatsApp from "@/components/layout/FloatingWhatsApp";
import { useLanguage } from "@/context/LanguageContext";
import SEOHead from "@/components/SEOHead";
import RecentlyViewed from "@/components/product/RecentlyViewed";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { t } = useLanguage();

  const { data: product, isLoading } = useProduct(id || "");
  const { data: similarProducts = [] } = useSimilarProducts(product || null);

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<"story" | "notes" | "usages">("story");
  const { addViewed } = useRecentlyViewed();

  useEffect(() => {
    if (product) addViewed(product.id);
  }, [product?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <p className="text-muted-foreground">{t.productDetail.notFound}</p>
        </div>
      </div>
    );
  }

  const currentPrice = product.sizes[selectedSize]?.price || product.price;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product, product.sizes[selectedSize]?.label || "Default", currentPrice);
    }
  };

  const usageItems = [
    { key: "winter", emoji: "❄️", label: t.productDetail.winter, value: product.usages.winter },
    { key: "spring", emoji: "🌸", label: t.productDetail.spring, value: product.usages.spring },
    { key: "summer", emoji: "☀️", label: t.productDetail.summer, value: product.usages.summer },
    { key: "fall", emoji: "🍂", label: t.productDetail.fall, value: product.usages.fall },
    { key: "day", emoji: "🌤️", label: t.productDetail.day, value: product.usages.day },
    { key: "night", emoji: "🌙", label: t.productDetail.night, value: product.usages.night },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <FloatingCart />
      <FloatingWhatsApp />

      {product && (
        <SEOHead
          title={product.name}
          description={product.story || `${product.name} — premium fragrance by Bait Al-Teeb`}
          canonical={`https://bait-al-teeb-essence.lovable.app/product/${product.id}`}
          type="product"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.story || product.name,
            image: product.images[0] || product.image,
            offers: {
              "@type": "Offer",
              price: product.price,
              priceCurrency: "EGP",
              availability: product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            },
          }}
        />
      )}
      <main className="pt-24 pb-20">
        <div className="container-luxury mb-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm tracking-wider uppercase">{t.productDetail.back}</span>
          </button>
        </div>

        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Images */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="aspect-[3/4] rounded-sm overflow-hidden mb-4 bg-card">
                <motion.img key={selectedImage} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} src={product.images[selectedImage] || product.image} alt={product.name} className="w-full h-full object-cover" />
              </div>
              {product.images.length > 1 && (
                <div className="grid grid-cols-5 gap-3">
                  {product.images.map((image, index) => (
                    <button key={index} onClick={() => setSelectedImage(index)} className={`aspect-square rounded-sm overflow-hidden border-2 transition-all ${selectedImage === index ? "border-primary" : "border-transparent hover:border-border"}`}>
                      <img src={image} alt={`${product.name} view ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col">
              <span className="text-primary text-sm tracking-[0.3em] uppercase mb-4">{product.category}</span>
              <h1 className="text-4xl md:text-5xl font-serif mb-2">{product.name}</h1>
              {product.nameArabic && <p className="text-2xl text-muted-foreground arabic mb-6" dir="rtl">{product.nameArabic}</p>}
              <div className="text-3xl font-serif text-primary mb-8">{currentPrice} {t.common.egp}</div>

              {product.sizes.length > 0 && (
                <div className="mb-8">
                  <label className="text-sm tracking-wider uppercase text-muted-foreground mb-3 block">{t.productDetail.selectSize}</label>
                  <div className="relative">
                    <select value={selectedSize} onChange={(e) => setSelectedSize(Number(e.target.value))} className="w-full appearance-none bg-card border border-border rounded-sm px-4 py-4 text-foreground focus:outline-none focus:border-primary transition-colors cursor-pointer">
                      {product.sizes
                        .map((size, index) => ({ ...size, originalIndex: index }))
                        .sort((a, b) => a.price - b.price)
                        .map((size) => (
                        <option key={size.label} value={size.originalIndex}>{size.label} — {size.price} {t.common.egp}</option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none rtl:right-auto rtl:left-4">
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-8">
                <label className="text-sm tracking-wider uppercase text-muted-foreground mb-3 block">{t.productDetail.quantity}</label>
                <div className="flex items-center gap-1">
                  <button onClick={() => quantity > 1 && setQuantity(quantity - 1)} disabled={quantity <= 1} className="w-12 h-12 border border-border rounded-sm flex items-center justify-center hover:border-primary hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <Minus className="w-5 h-5" />
                  </button>
                  <div className="w-16 h-12 border border-border rounded-sm flex items-center justify-center bg-card">
                    <span className="text-xl font-medium">{quantity}</span>
                  </div>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-12 h-12 border border-border rounded-sm flex items-center justify-center hover:border-primary hover:bg-muted/50 transition-colors">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex gap-4 mb-10">
                <Button variant="bronze" size="xl" className="flex-1" onClick={handleAddToCart}>
                  <ShoppingBag className="w-5 h-5" />{t.productDetail.addToCart}
                </Button>
                <Button variant="elegant" size="xl" className="px-6" onClick={() => product && toggleWishlist(product.id)}>
                  <Heart className={`w-5 h-5 transition-colors ${product && isInWishlist(product.id) ? "fill-primary text-primary" : ""}`} />
                </Button>
              </div>

              <div className="divider-gold mb-10" />

              {/* Tabs */}
              <div className="mb-6">
                <div className="flex gap-8 border-b border-border">
                  {([
                    { key: "story", label: t.productDetail.story },
                    { key: "notes", label: t.productDetail.ingredients },
                    { key: "usages", label: t.productDetail.usages },
                  ] as const).map((tab) => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`pb-4 text-sm tracking-wider uppercase transition-colors relative ${activeTab === tab.key ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                      {tab.label}
                      {activeTab === tab.key && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                {activeTab === "story" ? (
                  <motion.div key="story" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <p className="text-muted-foreground leading-relaxed" dir="auto">{product.story || "A beautiful fragrance crafted with care."}</p>
                  </motion.div>
                ) : activeTab === "notes" ? (
                  <motion.div key="notes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      {([
                        { key: "top", label: t.productDetail.topNotes, notes: product.notes.top },
                        { key: "middle", label: t.productDetail.heartNotes, notes: product.notes.middle },
                        { key: "base", label: t.productDetail.baseNotes, notes: product.notes.base },
                      ]).map((section) => (
                        <div key={section.key}>
                          <h4 className="text-sm tracking-wider uppercase text-primary mb-2">{section.label}</h4>
                          <ul className="space-y-1">
                            {section.notes.length > 0 ? section.notes.map((note) => (
                              <li key={note} className="text-muted-foreground text-sm" dir="auto">{note}</li>
                            )) : <li className="text-muted-foreground/50 text-sm">—</li>}
                          </ul>
                        </div>
                      ))}
                    </div>
                    {product.ingredients.length > 0 && (
                      <div>
                        <h4 className="text-sm tracking-wider uppercase text-primary mb-2">{t.productDetail.keyIngredients}</h4>
                        <div className="flex flex-wrap gap-2">
                          {product.ingredients.map((ingredient) => (
                            <span key={ingredient} className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-sm" dir="auto">{ingredient}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div key="usages" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
                    <div>
                      <h4 className="text-sm tracking-wider uppercase text-primary mb-4">{t.productDetail.whenToWear}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {usageItems.map((item) => (
                          <div key={item.key} className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">{item.emoji}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs text-muted-foreground block mb-1">{item.label}</span>
                              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${item.value}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {similarProducts.length > 0 && (
          <section className="container-luxury mt-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <span className="text-primary text-sm tracking-[0.3em] uppercase">Discover More</span>
              <h2 className="text-3xl md:text-4xl font-serif mt-4">{t.productDetail.similar}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {similarProducts.map((p, index) => <ProductCard key={p.id} product={p} index={index} />)}
            </div>
          </section>
        )}

        <RecentlyViewed excludeId={product?.id} />
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
